import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "./ui/input";
import { KeyRound, Plus, Trash2, Copy, Eye, EyeOff } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

const mask = (k) => `${k.slice(0, 10)}••••••••${k.slice(-4)}`;

export const ApiKeysSection = ({ onChanged }) => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [shown, setShown] = useState({});
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = () => api.get("/apikeys").then((r) => { setKeys(r.data.data); onChanged?.(); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Anahtar adı girin (örn. Control Tower)");
    setCreating(true);
    try {
      const r = await api.post("/apikeys", { name: name.trim() });
      await navigator.clipboard.writeText(r.data.key).catch(() => {});
      toast.success("API anahtarı oluşturuldu ve panoya kopyalandı");
      setName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const copy = (k) => {
    navigator.clipboard.writeText(k).then(() => toast.success("Anahtar kopyalandı")).catch(() => toast.error("Kopyalanamadı"));
  };

  const remove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/apikeys/${removeTarget.id}`);
      toast.success("Anahtar silindi — bu anahtarla erişim anında kesildi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    } finally {
      setRemoveTarget(null);
    }
  };

  return (
    <div className="border border-stone-200 bg-white p-4" data-testid="api-keys-section">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C4E35]">
        <KeyRound size={14} /> API Erişimi (Dış Sistemler)
      </p>
      <p className="mt-1 text-xs text-stone-500">
        Oluşturduğunuz anahtarı dış sisteme verin; istekler <code className="bg-stone-100 px-1">X-API-Key</code> başlığıyla yapılır (sadece okuma). Tüm uçlar aşağıdaki Endpoint Kataloğu'nda listelenir.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Anahtar adı (örn. Control Tower entegrasyonu)" data-testid="apikey-name" className="h-9 w-72 rounded-sm border-stone-300 text-xs" />
        <button type="button" onClick={create} disabled={creating} data-testid="apikey-create" className="flex items-center gap-1.5 bg-[#1C4E35] px-3 py-2 text-xs font-bold text-white hover:bg-[#143A26] disabled:opacity-50">
          <Plus size={13} /> Anahtar Oluştur
        </button>
      </div>

      <div className="mt-3 divide-y divide-stone-100 border border-stone-100">
        {keys.length === 0 && <p className="px-3 py-2 text-xs text-stone-400" data-testid="apikey-empty">Henüz API anahtarı yok.</p>}
        {keys.map((k) => (
          <div key={k.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2" data-testid={`apikey-row-${k.id}`}>
            <span className="text-xs font-medium text-[#0A0D0B]">{k.name}</span>
            <code className="bg-stone-50 px-1.5 py-0.5 text-[11px] text-stone-600" data-testid={`apikey-value-${k.id}`}>{shown[k.id] ? k.key : mask(k.key)}</code>
            <button type="button" onClick={() => setShown((s) => ({ ...s, [k.id]: !s[k.id] }))} className="text-stone-400 hover:text-stone-700" title={shown[k.id] ? "Gizle" : "Göster"} data-testid={`apikey-toggle-${k.id}`}>
              {shown[k.id] ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
            <button type="button" onClick={() => copy(k.key)} className="text-stone-400 hover:text-[#1C4E35]" title="Kopyala" data-testid={`apikey-copy-${k.id}`}>
              <Copy size={13} />
            </button>
            <span className="ml-auto text-[10px] text-stone-400">
              {k.request_count || 0} istek{k.last_used_at ? ` · son: ${new Date(k.last_used_at).toLocaleString("tr-TR")}` : ""}
            </span>
            <button type="button" onClick={() => setRemoveTarget(k)} className="border border-red-300 bg-red-50 p-1 text-red-700 hover:bg-red-600 hover:text-white" title="Sil" data-testid={`apikey-delete-${k.id}`}>
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="apikey-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>API anahtarını silmek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget && <><strong>{removeTarget.name}</strong> anahtarı silinecek ve bu anahtarı kullanan dış sistemlerin erişimi anında kesilecek.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="apikey-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="apikey-delete-confirm" className="bg-red-600 text-white hover:bg-red-700">Evet, Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
